import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SupportChat from '../chat';
import { getTicket, sendMessage } from '../../../stores/support.store';
import { toast } from 'react-toastify';
import { MainContext } from '../../../context/main.context';
import {
    INewTicketMessageSupport,
    ITicketMessageMediaSupport,
    ITicketMessageSupport,
    ITicketSupport,
} from '../interfaces';
import { useTranslation } from 'react-i18next';
import { getAuthElemStyle } from '../../../common/tools';
import Header from '../../header';
import Footer from '../../footer';
import SupportTickets from '../tickets';

const CHAT_ID: string = 'ticket-item';
const GET_TICKET_INTERVAL: number = 60000; // 1 минута

interface Props {
    ticketId?: string
}


export default function SupportTicket(props: Props) {
    const [ searchParams ] = useSearchParams();
    const id: string | null = searchParams.get('id');

    const ticketId: string = props?.ticketId || id as string;

    const {
        toggleLoader,
        modalContent,
        settings,
        setModalContent,
        getSupportUnreadMessageCountPublic
    } = useContext(MainContext);

    const navigate = useNavigate();
    const { t } = useTranslation();
    let intervalId: NodeJS.Timeout | null = null;

    const [ ticket, setTicket ] = useState<ITicketSupport | null>(null);

    const isClosed = useMemo(() => Boolean(ticket?.status === 'closed'), [ ticket ]);

    useEffect(() => {
        _getTicket();

        intervalId = setInterval(_getTicket, GET_TICKET_INTERVAL);

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, []);

    const _getTicket = () => {
        toggleLoader(true);

        getTicket(ticketId).then(res => {
            const ticketData: ITicketSupport = res?.data;

            if (ticketData) {
                setTicket(ticketData);

                setTimeout(() => {
                    const elem = document.getElementById(CHAT_ID) as HTMLElement;
                    elem.scrollTo(0, elem.scrollHeight);
                }, 200);

                getSupportUnreadMessageCountPublic();

                return;
            }

            if (settings?.projects?.authType === 'postMessage') {
                setModalContent({ component: <SupportTickets/>, className: 'support-tickets-modal' });
                return;
            }

            navigate('/support/tickets');
        }, err => {
            toast.error('GET_TICKET_ERROR');
            console.error('GET_TICKET_ERROR', { err })
        }).finally(() => {
            toggleLoader(false);
        });
    }

    const _onSubmit = (text: string | null, image: File | null = null, video: File | null = null) => {
        const formData: FormData = new FormData();

        const newMessage: INewTicketMessageSupport = {
            ticketId,
        }

        if (text) {
            newMessage.text = text;
        }

        if (image) {
            formData.append('image', image as File);
        }

        if (video) {
            formData.append('video', video as File);
        }

        formData.append('data', JSON.stringify(newMessage));

        _sendMessage(formData);
    }

    const _sendMessage = (formData: FormData) => {
        toggleLoader(true);

        sendMessage(formData).then(res => {
            const { result } = res?.data;

            if (!result) {
                toast.error('SEND_MESSAGE_ERROR');
                console.error('SEND_MESSAGE_ERROR', { res });
                return;
            }

            _getTicket();
        }, err => {
            if (err?.response?.data?.message === 'HttpException: TICKET_CLOSED') {
                _getTicket();
                return;
            }

            toast.error('SEND_MESSAGE_ERROR');
            console.error('SEND_MESSAGE_ERROR', { err })
        }).finally(() => {
            toggleLoader(false);
        });
    }

    const _renderTicketMessages = useCallback(() => {
        return (
            <div className="support-ticket-chat-container-msg-list" id={CHAT_ID}>
                {
                    ticket?.messages.length ?
                        ticket.messages.map((msg: ITicketMessageSupport, index: number) => {
                            const { id, isUserMsg, text, image, video, createTs, isAutomated } = msg;

                            let ticketPosition: string = 'ticket-left';
                            let author: string = t('profile.support');
                            let containerClassName: string = '';

                            if (isAutomated) {
                                ticketPosition = 'ticket-center';
                                author = '';
                                containerClassName = 'support-ticket-chat-container-msg-system';
                            } else if (isUserMsg) {
                                ticketPosition = 'ticket-right';
                                author = t('support.you');
                                containerClassName = 'support-ticket-chat-container-msg-user';
                            }

                            return (
                                <div
                                    key={`${id}_${index}`}
                                    className={`support-ticket-chat-container-item ${ticketPosition}`}
                                >
                                    <div className={`support-ticket-chat-container-msg ${containerClassName}`}>
                                        <div className="support-ticket-chat-container-author text-overflow">
                                            { author }
                                        </div>
                                        { _renderTicketMessageData(text, image, video, isAutomated) }
                                        <div className="support-ticket-chat-container-date text-overflow">
                                            { new Date(createTs).toLocaleString() }
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    : null
                }
                {
                    isClosed && (
                        <div className="support-ticket-chat-container-msg-list-closed">
                            <span className="support-ticket-chat-container-msg-list-closed-text">
                                { t('support.conversationEnded') }
                            </span>
                        </div>
                    )
                }
            </div>
        );
    }, [ isClosed, ticket?.messages ]);

    const _renderTicketMessageData = (
        text: string | null,
        image: ITicketMessageMediaSupport | null,
        video: ITicketMessageMediaSupport | null,
        isAutomated: boolean
    ) => {
        if (isAutomated) {
            return (
                <div className="support-ticket-chat-container-content-automated">
                    { text }
                </div>
            );
        }

        const imagePath: string = `${window.location.origin}${image?.path}`;
        const videoPath: string = `${window.location.origin}${video?.path}`;

        return (
            <div className="support-ticket-chat-container-content">
                { text && <div dangerouslySetInnerHTML={{__html: text}}/> }
                { image && ( <img src={imagePath} alt=""/>) }
                { video && <video controls className="" src={videoPath}/> }
            </div>
        );
    }

    const _renderTicketChat = () => {
        return (
            <SupportChat
                idValue={CHAT_ID}
                classNameValue="support-ticket-chat-container-ql"
                onSubmit={_onSubmit}
                readOnly={isClosed}
            />
        );
    }

    return (
        <div
            className="auth support"
            style={{ background: !modalContent ? '#e8e8e8' : 'unset' }}
        >
            <div
                className="auth-container"
                style={{
                    background: !modalContent ? '#e8e8e8' : 'unset',
                    minHeight: !modalContent ? 'calc(100vh - 100px)' : '100%',
                }}
            >
                <div className="auth-modal support" style={getAuthElemStyle(settings)}>
                    <Header origin="ticketItem"/>
                    <div className="support-ticket-chat-container">
                        {_renderTicketMessages()}
                        {_renderTicketChat()}
                    </div>
                </div>
            </div>
            { !modalContent && <Footer/> }
        </div>
    );
}